import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { config } from '../config';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    tenants: 0,
    users: 0,
    tenantAdmins: 0
  });
  const [usageStats, setUsageStats] = useState({
    totalSuccessful: 0,
    totalFailed: 0,
    uniqueUsers: 0,
    dailyStats: []
  });
  const [failedLogins, setFailedLogins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMessage, setImportMessage] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [overwriteTenants, setOverwriteTenants] = useState(false);

  useEffect(() => {
    loadUser();
    loadStats();
    loadUsageStats();
    loadFailedLogins();
  }, []);

  const loadUser = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await fetch(`${config.API_URL}/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
        return;
      }

      const data = await response.json();
      setUser(data);

      if (data.role !== 'admin') {
        navigate('/');
      }
    } catch (error) {
      console.error('Error loading user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const [tenantsRes, usersRes] = await Promise.all([
        fetch(`${config.API_URL}/admin/tenants`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }),
        fetch(`${config.API_URL}/users`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })
      ]);

      const tenants = await tenantsRes.json();
      const users = await usersRes.json();

      setStats({
        tenants: tenants.length,
        users: users.length,
        tenantAdmins: users.filter(u => u.role === 'tenantAdmin').length
      });
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const loadUsageStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/admin/usage-stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error('Error loading usage stats:', error);
    }
  };

  const loadFailedLogins = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/admin/login-logs?limit=50`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setFailedLogins(data);
      }
    } catch (error) {
      console.error('Error loading failed logins:', error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tenantId');
    navigate('/login');
  };

  const handleExportTenants = async () => {
    try {
      setExporting(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/admin/tenants/export`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Export fehlgeschlagen');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileName = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
        : `tenants-export-${new Date().toISOString().split('T')[0]}.tar.gz`;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting tenants:', error);
      alert('Fehler beim Exportieren: ' + error.message);
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.tar.gz') && !file.name.endsWith('.tgz')) {
      setImportMessage({ type: 'error', text: 'Nur .tar.gz Dateien sind erlaubt' });
      e.target.value = '';
      return;
    }

    setImportFile(file);
    setShowImportModal(true);
    e.target.value = '';
  };

  const handleConfirmImport = async () => {
    if (!importFile) return;

    try {
      setImporting(true);
      setImportMessage(null);
      setShowImportModal(false);

      const formData = new FormData();
      formData.append('file', importFile);
      formData.append('overwrite', overwriteTenants.toString());

      const token = localStorage.getItem('token');
      const response = await fetch(`${config.API_URL}/admin/tenants/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Import fehlgeschlagen');
      }

      const result = await response.json();
      
      let message = `Import erfolgreich: ${result.imported} Tenants importiert`;
      if (result.skipped > 0) {
        message += `, ${result.skipped} übersprungen`;
      }
      if (result.errors > 0) {
        message += `, ${result.errors} Fehler`;
      }
      
      setImportMessage({ type: 'success', text: message });
      setImportFile(null);
      setOverwriteTenants(false);
      
      if (result.errors > 0 && result.errors) {
        console.error('Import errors:', result.errors);
      }
      
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error importing tenants:', error);
      setImportMessage({ type: 'error', text: 'Fehler beim Importieren: ' + error.message });
      setImportFile(null);
      setOverwriteTenants(false);
    } finally {
      setImporting(false);
    }
  };

  const handleCancelImport = () => {
    setShowImportModal(false);
    setImportFile(null);
    setOverwriteTenants(false);
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-header-actions">
          <span>Angemeldet als: <strong>{user?.username}</strong></span>
          <button type="button" onClick={handleLogout} className="btn-secondary">
            Abmelden
          </button>
        </div>
      </div>

      <div className="admin-stats">
        <div className="stat-card">
          <h3>Tenants</h3>
          <p className="stat-number">{stats.tenants}</p>
        </div>
        <div className="stat-card">
          <h3>Benutzer</h3>
          <p className="stat-number">{stats.users}</p>
        </div>
        <div className="stat-card">
          <h3>Tenant Admins</h3>
          <p className="stat-number">{stats.tenantAdmins}</p>
        </div>
        <div className="stat-card">
          <h3>Erfolgreiche Logins</h3>
          <p className="stat-number">{usageStats.totalSuccessful}</p>
        </div>
        <div className="stat-card">
          <h3>Fehlgeschlagene Logins</h3>
          <p className="stat-number" style={{ color: '#d32f2f' }}>{usageStats.totalFailed}</p>
        </div>
        <div className="stat-card">
          <h3>Aktive Benutzer</h3>
          <p className="stat-number">{usageStats.uniqueUsers}</p>
        </div>
      </div>

      <div className="admin-actions">
        <button type="button" onClick={() => navigate('/admin/tenants')} className="btn-primary">
          Tenants verwalten
        </button>
        <button 
          type="button" 
          onClick={() => {
            console.log('Button clicked, navigating to /admin/users');
            navigate('/admin/users');
          }} 
          className="btn-primary"
        >
          Benutzer verwalten
        </button>
        <button type="button" onClick={handleExportTenants} className="btn-secondary" disabled={exporting}>
          {exporting ? 'Exportiere...' : 'Tenants exportieren'}
        </button>
        <label className="btn-secondary" style={{ cursor: importing ? 'not-allowed' : 'pointer', opacity: importing ? 0.6 : 1 }}>
          {importing ? 'Importiere...' : 'Tenants importieren'}
          <input
            type="file"
            accept=".tar.gz,.tgz"
            onChange={handleFileSelect}
            disabled={importing}
            style={{ display: 'none' }}
          />
        </label>
      </div>

      {showImportModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{ marginTop: 0 }}>Tenants importieren</h2>
            <p style={{ marginBottom: '1rem' }}>
              Datei: <strong>{importFile?.name}</strong>
            </p>
            <p style={{ marginBottom: '1.5rem', color: '#666' }}>
              Möchten Sie diese Tenants wirklich importieren? Bestehende Tenants mit gleichem Namen und Adresse werden {overwriteTenants ? 'überschrieben' : 'übersprungen'}.
            </p>
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={overwriteTenants}
                  onChange={(e) => setOverwriteTenants(e.target.checked)}
                  style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                />
                <span>Bestehende Tenants überschreiben</span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={handleCancelImport}
                className="btn-secondary"
                disabled={importing}
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleConfirmImport}
                className="btn-primary"
                disabled={importing}
              >
                Importieren
              </button>
            </div>
          </div>
        </div>
      )}

      {importMessage && (
        <div className={`import-message ${importMessage.type === 'success' ? 'success' : 'error'}`} style={{
          margin: '20px',
          padding: '15px',
          borderRadius: '4px',
          backgroundColor: importMessage.type === 'success' ? '#d4edda' : '#f8d7da',
          color: importMessage.type === 'success' ? '#155724' : '#721c24',
          border: `1px solid ${importMessage.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`
        }}>
          {importMessage.text}
        </div>
      )}

      <div className="admin-sections">
        <div className="admin-section">
          <h2>Fehlgeschlagene Logins</h2>
          <div className="login-logs-table">
            <table>
              <thead>
                <tr>
                  <th>Zeitpunkt</th>
                  <th>Benutzername</th>
                  <th>Grund</th>
                  <th>IP-Adresse</th>
                </tr>
              </thead>
              <tbody>
                {failedLogins.length === 0 ? (
                  <tr>
                    <td colSpan="4" style={{ textAlign: 'center', color: '#666' }}>
                      Keine fehlgeschlagenen Logins
                    </td>
                  </tr>
                ) : (
                  failedLogins.map(log => (
                    <tr key={log.id}>
                      <td>{formatDate(log.timestamp)}</td>
                      <td>{log.username}</td>
                      <td>{log.reason === 'User not found' ? 'Benutzer nicht gefunden' : 'Falsches Passwort'}</td>
                      <td>{log.ipAddress || '-'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="admin-section">
          <h2>Login-Statistiken (letzte 30 Tage)</h2>
          <div className="usage-stats-chart">
            {usageStats.dailyStats.length === 0 ? (
              <p style={{ color: '#666', textAlign: 'center' }}>Keine Daten verfügbar</p>
            ) : (
              <div className="daily-stats">
                {usageStats.dailyStats.map(day => (
                  <div key={day.date} className="daily-stat-item">
                    <div className="daily-stat-date">
                      {new Date(day.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
                    </div>
                    <div className="daily-stat-bars">
                      <div className="stat-bar success" style={{ width: `${Math.max(day.successful * 2, 5)}%` }}>
                        {day.successful > 0 && <span>{day.successful}</span>}
                      </div>
                      <div className="stat-bar failed" style={{ width: `${Math.max(day.failed * 2, 5)}%` }}>
                        {day.failed > 0 && <span>{day.failed}</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;

